import type { RoomView, SeatView } from '@chipless/shared';
import { Badge, Card, Group, Paper, Stack, Text } from '@mantine/core';
import { pt, ptWithUnit, seatLabel, streetLabel } from '../format.js';

/**
 * 手元の簡易ビュー。
 *
 * 中央ディスプレイは必須だが、手元でも状況が追えないと卓から目を離せなくなるため
 * 省かない（design 9.2）。
 */

function stateBadge(seat: SeatView): React.ReactElement | null {
  if (seat.state === 'FOLDED') {
    return (
      <Badge size="xs" color="gray" variant="light">
        フォールド
      </Badge>
    );
  }
  if (seat.state === 'ALLIN') {
    return (
      <Badge size="xs" color="orange" variant="light">
        オールイン
      </Badge>
    );
  }
  if (seat.state === 'NEEDS_REBUY') {
    return (
      <Badge size="xs" color="red" variant="light">
        リバイ必要
      </Badge>
    );
  }
  return null;
}

export function TableSummary({ view }: { view: RoomView }): React.ReactElement {
  const hand = view.hand;
  const mySeatNo = view.you.seatNo;
  const mySeat = mySeatNo === null ? undefined : view.seats[mySeatNo];

  return (
    <Stack p="md" gap="md">
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              ポット
            </Text>
            <Text
              fw={900}
              lh={1}
              style={{ fontSize: 'calc(var(--mantine-font-size-xl) * 1.9)' }}
              data-testid="player-pot"
            >
              {/* ハンドが無いときも 0。値が定まる欄に「—」は使わない（中央ディスプレイと同じ）。 */}
              {pt(hand?.potTotal ?? 0)}
            </Text>
          </Stack>
          <Stack gap={2} align="flex-end">
            <Text size="xs" c="dimmed">
              自分のスタック
            </Text>
            <Text
              fw={700}
              style={{ fontSize: 'calc(var(--mantine-font-size-xl) * 1.2)' }}
              data-testid="my-stack"
            >
              {mySeat === undefined ? '—' : pt(mySeat.stack)}
            </Text>
            {mySeat !== undefined && mySeat.pendingBuyIn > 0 && (
              <Badge size="xs" color="teal" variant="light" data-testid="my-pending">
                次ハンド +{pt(mySeat.pendingBuyIn)}
              </Badge>
            )}
          </Stack>
        </Group>

        <Group mt="sm" gap="xs">
          <Badge variant="light" color={hand === null ? 'gray' : 'teal'}>
            {hand === null ? 'ハンド待機' : streetLabel(hand.street)}
          </Badge>
          <Badge variant="light" color="gray">
            {ptWithUnit(view.config.smallBlind)} / {ptWithUnit(view.config.bigBlind)}
          </Badge>
          {hand !== null && hand.currentBet > 0 && (
            <Badge variant="light" color="yellow">
              コール必要額 {pt(hand.currentBet - (mySeat?.streetBet ?? 0))}
            </Badge>
          )}
        </Group>

        {hand !== null && hand.pots.length > 1 && (
          <Stack gap={2} mt="xs">
            {hand.pots.map((p, i) => (
              <Text key={i} size="xs" c="dimmed">
                {i === 0 ? 'メイン' : `サイド${i}`} {ptWithUnit(p.amount)}（
                {p.eligible.map((s) => seatLabel(s)).join('・')}）
              </Text>
            ))}
          </Stack>
        )}
      </Paper>

      <Stack gap="xs">
        {view.seats
          .filter((seat) => seat.occupied)
          .map((seat) => (
            <Card
              key={seat.seatNo}
              withBorder
              padding="xs"
              radius="md"
              bg={seat.isTurn ? 'yellow.9' : 'transparent'}
              // フォールドした席も名前とスタックは読み取る対象なので、薄くしすぎない。
              opacity={seat.state === 'FOLDED' ? 0.62 : 1}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap">
                  {seat.isButton && (
                    <Badge size="xs" circle color="gray" variant="filled">
                      D
                    </Badge>
                  )}
                  <Text fw={seat.seatNo === mySeatNo ? 700 : 500} truncate>
                    {seat.name}
                    {seat.seatNo === mySeatNo ? '（自分）' : ''}
                  </Text>
                  {!seat.connected && (
                    <Badge size="xs" color="red" variant="outline">
                      切断
                    </Badge>
                  )}
                  {stateBadge(seat)}
                </Group>
                <Group gap="sm" wrap="nowrap">
                  {seat.streetBet > 0 && (
                    <Badge size="sm" color="yellow" variant="filled">
                      {pt(seat.streetBet)}
                    </Badge>
                  )}
                  <Text fw={700}>{pt(seat.stack)}</Text>
                </Group>
              </Group>
            </Card>
          ))}
      </Stack>
    </Stack>
  );
}
